import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, Briefcase, Users, CheckCircle, Building2, Clock, ArrowRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '../services/api';
import { formatCurrency } from '../utils/format';

const STAGE_COLORS = {
  lead: '#6366F1', qualified: '#3B82F6', proposal: '#FFAB00',
  negotiation: '#F97316', won: '#1B99E5', lost: '#EF4444'
};

const STAGE_LABELS = {
  lead: 'Lead', qualified: 'Qualifié', proposal: 'Proposition',
  negotiation: 'Négociation', won: 'Gagné', lost: 'Perdu'
};

const KPICard = ({ icon: Icon, title, value, subtitle, color, testId, onClick }) => (
  <div
    className={`bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5 crm-card ${onClick ? 'cursor-pointer hover:border-[#1B99E5]/40 hover:shadow-md' : ''}`}
    style={{ transition: 'box-shadow 200ms ease, border-color 200ms ease' }}
    onClick={onClick}
    data-testid={testId}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      {onClick && <ArrowRight size={14} className="text-gray-300 dark:text-gray-600 mt-1" />}
    </div>
    <p className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-0.5">{title}</p>
    {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-[#1A1D23] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="text-xs">{p.name}: <strong>{typeof p.value === 'number' && p.name?.includes('€') ? `${p.value.toLocaleString('fr-FR')} €` : p.value}</strong></p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/reports/summary'),
      api.get('/reports/monthly'),
      api.get('/activities?completed=false')
    ]).then(([s, m, a]) => {
      setStats(s.data);
      setMonthly(m.data.map(d => ({
        month: d._id,
        deals: d.count,
        'Valeur (€)': Math.round(d.value),
        'Gagné (€)': Math.round(d.won)
      })));
      setRecentActivities(a.data.slice(0, 5));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const pieData = stats?.deals_by_stage?.map(d => ({
    name: STAGE_LABELS[d._id] || d._id,
    value: d.count,
    color: STAGE_COLORS[d._id] || '#94a3b8'
  })) || [];

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5 h-28 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Tableau de bord
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Vue d'ensemble de votre performance commerciale
          </p>
        </div>
        <Button
          onClick={() => navigate('/pipeline')}
          className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-9 px-4 text-sm font-medium rounded-lg active:scale-95"
          style={{ transition: 'background-color 150ms ease, transform 100ms ease' }}
          data-testid="go-to-pipeline-btn"
        >
          <Plus size={15} className="mr-1.5" /> Nouveau devis
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={TrendingUp} title="Chiffre d'affaires" testId="kpi-revenue"
          value={formatCurrency(stats?.total_revenue)} color="bg-[#1B99E5]"
          subtitle={`${stats?.won_deals || 0} devis gagnés`}
          onClick={() => navigate('/reports')}
        />
        <KPICard
          icon={Briefcase} title="Devis actifs" testId="kpi-deals"
          value={stats?.total_deals || 0} color="bg-[#3B82F6]"
          subtitle={`Valeur pipeline: ${formatCurrency(stats?.pipeline_value)}`}
          onClick={() => navigate('/deals')}
        />
        <KPICard
          icon={Users} title="Contacts" testId="kpi-contacts"
          value={stats?.total_contacts || 0} color="bg-[#6366F1]"
          subtitle={`${stats?.total_companies || 0} entreprises`}
          onClick={() => navigate('/contacts')}
        />
        <KPICard
          icon={CheckCircle} title="Taux de conversion" testId="kpi-conversion"
          value={`${stats?.conversion_rate || 0}%`} color="bg-[#FFAB00]"
          subtitle={`${stats?.pending_activities || 0} activités en attente`}
          onClick={() => navigate('/reports')}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly chart */}
        <div className="lg:col-span-2 bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Évolution des devis
          </h3>
          {monthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthly} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Valeur (€)" fill="#1B99E5" radius={[4,4,0,0]} />
                <Bar dataKey="Gagné (€)" fill="#3B82F6" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-400">
              <Briefcase size={32} className="mb-2 opacity-30" />
              <p className="text-sm">Aucune donnée disponible</p>
              <Button variant="ghost" size="sm" className="mt-2 text-[#1B99E5]" onClick={() => navigate('/pipeline')}>
                Ajouter des deals <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          )}
        </div>

        {/* Pie chart */}
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Devis par étape
          </h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="45%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-400">
              <p className="text-sm">Aucun deal</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent activities */}
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Activités en attente
            </h3>
            <Button variant="ghost" size="sm" className="text-xs text-[#1B99E5]" onClick={() => navigate('/activities')}>
              Voir tout <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
          {recentActivities.length > 0 ? (
            <div className="space-y-2">
              {recentActivities.map(act => (
                <div key={act.id} className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0" data-testid={`activity-item-${act.id}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    act.type === 'call' ? 'bg-green-100 text-green-600' :
                    act.type === 'email' ? 'bg-blue-100 text-blue-600' :
                    act.type === 'meeting' ? 'bg-purple-100 text-purple-600' :
                    act.type === 'task' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {act.type.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{act.title}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{act.contact_name || act.deal_title || ''}</p>
                  </div>
                  {act.due_date && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                      <Clock size={11} />
                      <span>{act.due_date}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex flex-col items-center justify-center text-gray-400">
              <CheckCircle size={28} className="mb-2 opacity-30" />
              <p className="text-sm">Aucune activité en attente</p>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Performance pipeline
          </h3>
          <div className="space-y-3">
            {[
              { label: 'Devis gagnés', value: stats?.won_deals || 0, total: stats?.total_deals || 1, color: '#1B99E5' },
              { label: 'Devis perdus', value: stats?.lost_deals || 0, total: stats?.total_deals || 1, color: '#EF4444' },
              { label: 'En cours', value: (stats?.total_deals || 0) - (stats?.won_deals || 0) - (stats?.lost_deals || 0), total: stats?.total_deals || 1, color: '#3B82F6' },
            ].map(({ label, value, total, color }) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">{value}</span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(100, (value/total)*100)}%`, backgroundColor: color, transition: 'width 600ms ease' }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Valeur totale pipeline</p>
              <p className="text-base font-bold text-gray-900 dark:text-white mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {formatCurrency(stats?.pipeline_value)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Entreprises actives</p>
              <p className="text-base font-bold text-gray-900 dark:text-white mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats?.total_companies || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
